import * as _ from 'lodash';
import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import type { VoidFunctionComponent } from 'react';

import { pluralizeIssues } from '../../../../../shared/helpers';
import { COUNT_MAX_FE } from '../../../../../shared/issuesV2';
import * as IssuesV2Store from '../../../../redux/issuesV2';
import {
  ButtonGroup,
  ErrorAlert,
  Icon,
  icons,
  Menu,
  MenuRadioGroup,
  RadioMenuItem,
} from '../../../generic';
import Button, { InlineLoaderButton } from '../../../generic/Button';
import useClearErrorsOnUnmount from '../hooks/useClearErrorsOnUnmount';
import type { AnyExportRequest, Category, ExportType, Issue } from '../../../../../shared/issuesV2';
import type { SetState } from '../../../../types';

import ExportIssueModalContent from './ExportIssueModalContent';
import ExportIssueModalWarning from './ExportIssueModalWarning';

const exportTypeOptions: Record<ExportType, string> = {
  jira: 'Create a JIRA ticket',
  custom: 'Link to an existing URL',
};

type Props = {
  isShowing: boolean;
  onHide: () => void;
  selectedIssuesCount: number;
  issueCategory: Category;
  onEnter?: () => void;
  issues: Issue[] | null;
  setContent: SetState<AnyExportRequest>;
  onExport: () => void;
  jiraTicketCount?: number;
  fetchIssuesErrors?: string[];
};

const ExportIssuesModal: VoidFunctionComponent<Props> = ({
  isShowing,
  onHide,
  selectedIssuesCount,
  issueCategory,
  onEnter,
  issues,
  setContent,
  onExport,
  jiraTicketCount,
  fetchIssuesErrors,
}) => {
  const [warningMessage, setWarningMessage] = useState('');
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [exportType, setExportType] = useState<ExportType>('jira');

  const { isLoading, errors } = useSelector(IssuesV2Store.selectors.getAsyncStatusUpdateIssues);

  useClearErrorsOnUnmount();

  return (
    <Modal
      show={isShowing}
      onHide={onHide}
      onEnter={onEnter}
      dialogClassName="issue-modal-wide"
      className="issues-modal issues-modal__wide"
    >
      <Modal.Header bsClass="issues-modal__header">
        <Modal.Title bsClass="issues-modal__header__title">Create Ticket</Modal.Title>
      </Modal.Header>
      <Modal.Body bsClass="issues-modal__body">
        <span className="issues-modal__body__message">
          {`${selectedIssuesCount.toLocaleString()} ${pluralizeIssues(
            selectedIssuesCount,
          )} will have a new ticket created via your issue tracker.`}
        </span>
        {selectedIssuesCount > COUNT_MAX_FE && (
          <ExportIssueModalWarning
            message={`There are ${selectedIssuesCount.toLocaleString()} selected issues. Ticket content will only include information from the first ${COUNT_MAX_FE} issues. All selected FOSSA issues will no longer be associated with their previous Jira ticket.`}
          />
        )}
        {warningMessage && <ExportIssueModalWarning message={warningMessage} />}
        <div className="issues-modal__body__type-picker">
          <div className="issues-modal__body__type-picker--header">
            Ticket type
          </div>
          <Menu
            menuButton={
              <Button type="secondary" onClick={_.noop}>
                {exportTypeOptions[exportType]} <Icon icon={icons.chevronDown} />
              </Button>
            }
            direction="bottom"
            >
            <MenuRadioGroup value={exportType} onRadioChange={(e) => setExportType(e.value)}>
              {Object.entries(exportTypeOptions).map(([type, label]) => (
                <RadioMenuItem value={type}>{label}</RadioMenuItem>
              ))}
            </MenuRadioGroup>
          </Menu>
        </div>
        <ExportIssueModalContent
          exportType={exportType}
          isShowing={isShowing}
          selectedIssuesCount={selectedIssuesCount}
          issueCategory={issueCategory}
          isLoading={isLoading}
          issues={issues}
          setWarningMessage={setWarningMessage}
          setContent={setContent}
          jiraTicketCount={jiraTicketCount}
          setIsSubmitDisabled={setIsSubmitDisabled}
        />
        {(errors || fetchIssuesErrors) && (
          <div className="issues-modal__body__errors">
            <ErrorAlert status={{ errors: errors ?? fetchIssuesErrors }} />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer bsClass="issues-modal__footer">
        <ButtonGroup>
          <Button type="secondary" size="medium" isDisabled={isLoading} onClick={onHide}>
            Cancel
          </Button>
          <InlineLoaderButton
            type="primary"
            size="medium"
            isSubmitButton
            isLoading={isLoading}
            isDisabled={isLoading || !issues || isSubmitDisabled}
            onClick={onExport}
          >
            Create ticket(s)
          </InlineLoaderButton>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  );
};

export default ExportIssuesModal;
